import * as Sentry from '@sentry/react';
import type { RouteObject } from 'react-router';
import { createBrowserRouter } from 'react-router';
import { capitalize } from 'es-toolkit';
import { ApplicationDomain } from './types';

const sentryCreateBrowserRouter: typeof createBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

// Regular expression to extract the pathname from a URL string
const reUrl = /https?:\/\/[^/]+(\/[^?#]*)/;

function getPathname(url: string | URL) {
  if (typeof url === 'string') {
    if (url.startsWith('http')) {
      try {
        // Try to use URL constructor first (modern browsers)
        const urlObject = new URL(url);
        return urlObject.pathname;
      } catch (_error) {
        // Fallback for older browsers like Safari 15
        // Extract pathname using regex with exec
        const match = reUrl.exec(url);
        return match ? match[1] : '';
      }
    }
    return url;
  }
  // If url is already a URL object
  return url.pathname;
}

// Helper to convert path segment to ApplicationDomain
function toApplicationDomain(path: string): ApplicationDomain | undefined {
  const capitalized = capitalize(path);

  switch (capitalized) {
    case 'Loans':
      return ApplicationDomain.Loans;
    case 'Retainers':
      return ApplicationDomain.Retainers;
    case 'Admin':
      return ApplicationDomain.Admin;
    case 'Profile':
      return ApplicationDomain.Profile;
    case 'Trading':
      return ApplicationDomain.Trading;
    case 'Dex':
      return ApplicationDomain.DEX;
    default:
      return undefined;
  }
}

class SkyRouter {
  router: ReturnType<typeof sentryCreateBrowserRouter> | null;

  constructor() {
    this.router = null;
  }

  init(routerConfig: RouteObject[], onHistoryChange: (pathname: string) => void) {
    if (this.router) {
      return this.router;
    }
    // React Router v8 runs native route middleware before loaders and threads a
    // `RouterContextProvider` through both, so no custom `dataStrategy` is needed.
    this.router = sentryCreateBrowserRouter(routerConfig);

    this.router.subscribe((state) => {
      const { historyAction } = state;
      const action = historyAction;
      switch (action.toString()) {
        case 'POP':
        case 'PUSH':
        case 'REPLACE':
          onHistoryChange(state.location.pathname);
          break;
        default:
          break;
      }
    });

    return this.router;
  }

  getDomain(url?: string | URL): ApplicationDomain | undefined {
    let domainPath: string | undefined;

    if (url) {
      [domainPath] = getPathname(url).split('/').filter(Boolean);
    } else {
      [domainPath] = this.router?.state.location.pathname.split('/').filter(Boolean) ?? [];
    }
    return domainPath ? toApplicationDomain(domainPath) : undefined;
  }

  getSection(url?: string | URL) {
    if (url) {
      return getPathname(url).split('/').filter(Boolean)[1];
    }
    return this.router?.state.location.pathname.split('/').filter(Boolean)[1];
  }

  getEntityId(url?: string | URL) {
    if (url) {
      return getPathname(url).split('/').filter(Boolean)[2];
    }
    return this.router?.state.location.pathname.split('/').filter(Boolean)[2];
  }

  getPathname(url?: string | URL) {
    if (url) {
      return getPathname(url);
    }
    return this.router?.state.location.pathname ?? '';
  }
}

export const skyRouter = new SkyRouter();
